import { createTheme } from '@mui/material/styles';
import { grey, red } from '@mui/material/colors';
import { getPageSurfaceConfig } from '../../../core/ui/pageSurface';

// Define color variables
const ChetwodeBlue = '#484DB5'; // Custom button color
const ChetwodeBlueDark = '#3c4f9a'; // Darker shade for hover or focus
const whiteColor = '#ffffff'; // White for contrast
const brightBlue = '#007bff'; // Bright blue for primary
const greyColor = '#6c757d'; // Grey for secondary elements
const blackColor = '#000000'; // Black for high contrast text
const darkBackground = '#1b2028';
const darkPaper = '#252b35';
const lightBlue = '#1e90ff'; // Light blue for dark backgrounds
const tomatoRed = '#ff6347'; // Tomato red for secondary elements
const lightErrorRed = red[300]; // Light red for error messages
const lightSurfaces = getPageSurfaceConfig();
const lightChrome = {
  appBarBg: lightSurfaces.pageBackground,
  appBarBorder: '#e8ecf1',
  brandText: '#243247',
  actionText: '#475569',
  actionBg: 'transparent',
  actionBorder: '#e2e8f0',
  actionHoverBg: '#f1f5f9',
  actionHoverText: '#1d4ed8',
};
const darkChrome = {
  appBarBg: darkBackground,
  appBarBorder: 'rgba(255,255,255,0.10)',
  brandText: '#f7fbff',
  actionText: '#f7fbff',
  actionBg: 'rgba(255,255,255,0.10)',
  actionBorder: 'rgba(255,255,255,0.18)',
  actionHoverBg: 'rgba(255,255,255,0.18)',
  actionHoverText: '#ffffff',
};

const createAccessibleInputOverrides = ({
  placeholderColor,
  disabledTextColor,
  disabledPlaceholderColor,
  disabledLabelColor,
  disabledBgColor,
  disabledBorderColor,
  missingFieldBgColor,
  missingFieldBorderColor,
  missingFieldLabelColor,
}) => ({
  MuiInputBase: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-input::placeholder': {
          WebkitTextFillColor: placeholderColor,
          color: placeholderColor,
          opacity: 1,
          fontStyle: 'italic',
        },
        '&.Mui-disabled': {
          color: disabledTextColor,
          backgroundColor: disabledBgColor,
        },
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: disabledTextColor,
          color: disabledTextColor,
          opacity: 1,
        },
        '& .MuiInputBase-input.Mui-disabled::placeholder': {
          WebkitTextFillColor: disabledPlaceholderColor,
          color: disabledPlaceholderColor,
          opacity: 1,
          fontStyle: 'italic',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          backgroundColor: disabledBgColor,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: disabledBorderColor,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: disabledLabelColor,
          opacity: 1,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: disabledLabelColor,
          opacity: 1,
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        '&.Mui-disabled': {
          WebkitTextFillColor: disabledTextColor,
          color: disabledTextColor,
          opacity: 1,
        },
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        // Edit mode cue: highlight only when label is not shrunk (field effectively empty).
        '&:not(:has(.Mui-focused)):has(.MuiInputLabel-root:not(.Mui-disabled):not(.MuiInputLabel-shrink)) .MuiOutlinedInput-root': {
          backgroundColor: missingFieldBgColor,
        },
        '&:not(:has(.Mui-focused)):has(.MuiInputLabel-root:not(.Mui-disabled):not(.MuiInputLabel-shrink)) .MuiOutlinedInput-notchedOutline': {
          borderColor: missingFieldBorderColor,
          borderStyle: 'dashed',
        },
        '&:not(:has(.Mui-focused)):has(.MuiInputLabel-root:not(.Mui-disabled):not(.MuiInputLabel-shrink)) .MuiInputLabel-root:not(.Mui-disabled):not(.MuiInputLabel-shrink)': {
          color: missingFieldLabelColor,
          fontWeight: 600,
        },
        // Read-only cue: empty disabled fields are highlighted as "not recorded".
        '&:has(.MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)) .MuiOutlinedInput-root': {
          backgroundColor: missingFieldBgColor,
        },
        '&:has(.MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)) .MuiOutlinedInput-notchedOutline': {
          borderColor: missingFieldBorderColor,
          borderStyle: 'dashed',
        },
        '&:has(.MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)) .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
          color: missingFieldLabelColor,
          fontWeight: 600,
          opacity: 1,
        },
      },
    },
  },
});

// Common Typography
const typography = {
  fontSize: 12,
  fontWeightRegular: 400,
  fontWeightBold: 700,
  h1: {
    fontSize: '12.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  fontFamily: "'Inter', sans-serif",
};

// Light Theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brightBlue, // Bright blue
      contrastText: whiteColor,
    },
    secondary: {
      main: greyColor, // Grey for secondary elements
      contrastText: whiteColor,
    },
    buttonColor: {
      main: ChetwodeBlue, // Custom button color
      dark: ChetwodeBlueDark, // Darker shade for hover
      contrastText: whiteColor, // White text for contrast
    },
    background: {
      default: lightSurfaces.appBackground,
      paper: lightSurfaces.elevatedPanel,
    },
    text: {
      primary: blackColor, // Black text for high contrast
      secondary: grey[700], // Slightly lighter grey for secondary text
    },
    error: {
      main: red[500], // Red for error messages
    },
  },
  typography,
  customSurfaces: lightSurfaces,
  customChrome: lightChrome,
  spacing: 8, // Consistent spacing
  components: {
    ...createAccessibleInputOverrides({
      placeholderColor: '#6b7280',
      disabledTextColor: '#233043',
      disabledPlaceholderColor: '#7f90a0',
      disabledLabelColor: '#394b59',
      disabledBgColor: '#f5f7fa',
      disabledBorderColor: '#9aa8b5',
      missingFieldBgColor: '#fff8e1',
      missingFieldBorderColor: '#e3a008',
      missingFieldLabelColor: '#9a3412',
    }),
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: lightSurfaces.appBackground,
        },
      },
    },
  },
});

// Dark Theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    divider: '#394352',
    primary: {
      main: lightBlue, // Light blue for dark backgrounds
      contrastText: whiteColor,
    },
    secondary: {
      main: tomatoRed, // Tomato red for secondary elements
      contrastText: whiteColor,
    },
    buttonColor: {
      main: ChetwodeBlue, // Custom button color
      dark: ChetwodeBlueDark, // Darker shade for hover
      contrastText: whiteColor, // White text for contrast
    },
    background: {
      default: darkBackground, // Very dark background
      paper: darkPaper, // Slightly lighter dark grey for elements
    },
    text: {
      primary: whiteColor, // White text for high contrast
      secondary: grey[400], // Lighter grey for secondary text
    },
    error: {
      main: lightErrorRed, // Light red for error messages
    },
  },
  typography,
  customSurfaces: {
    appBackground: darkBackground,
    pageBackground: darkBackground,
    pageBackgroundGradient: darkBackground,
    sectionBackground: darkBackground,
    elevatedPanel: darkPaper,
    elevatedPanelGradient: darkPaper,
    border: 'rgba(255,255,255,0.10)',
  },
  customChrome: darkChrome,
  spacing: 8, // Consistent spacing
  components: {
    ...createAccessibleInputOverrides({
      placeholderColor: '#9db0bf',
      disabledTextColor: '#e6edf3',
      disabledPlaceholderColor: '#8fa3b5',
      disabledLabelColor: '#c5d0da',
      disabledBgColor: '#253341',
      disabledBorderColor: '#6f8599',
      missingFieldBgColor: '#3b2f1a',
      missingFieldBorderColor: '#e3a008',
      missingFieldLabelColor: '#f4c46b',
    }),
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: darkBackground,
        },
      },
    },
  },
});
